import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AddFormFieldButton } from '@/components/Buttons/AddFormFieldButton/AddFormFieldButton';
import type { HcpDcrValuesIn } from '@/features/DataChangeRequest/_schemas/hcpSchema';
import { FormSection } from '@/features/DataChangeRequest/DcrModal/FormSection/FormSection';

import { hcpLicensesSectionStyles as sx } from './HcpLicensesSection.styles';
import { LicensesSectionCard } from './LicenseSectionCard/LicenseSectionCard';

type LicenseSnapshot = NonNullable<HcpDcrValuesIn['licenses']>[number];

export const HcpLicensesSection = () => {
	const { t } = useTranslation();
	const { control } = useFormContext<HcpDcrValuesIn>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'licenses',
		keyName: 'key'
	});

	const [initialSnapshots, setInitialSnapshots] = useState<Record<
		string,
		LicenseSnapshot
	> | null>(null);

	useEffect(() => {
		if (initialSnapshots) return;

		if (fields.length > 0) {
			const snapshot: Record<string, LicenseSnapshot> = {};
			const stableData = JSON.parse(JSON.stringify(fields));

			stableData.forEach((field: LicenseSnapshot) => {
				if (field.vid_V) {
					snapshot[field.vid_V] = field;
				}
			});

			// eslint-disable-next-line react-hooks/set-state-in-effect
			setInitialSnapshots(snapshot);
		}

		if (fields.length === 0) {
			setInitialSnapshots({});
		}
	}, [fields, initialSnapshots]);

	const handleAddLicense = () => {
		append(
			{
				vid_V: '',

				// Editable Defaults
				licenseNumber_V: '',
				typeValue_VId: '',
				expirationDate_V: '',

				// Initialize read-only fields as null
				body_V: null,
				bestStateLicense_V: null,
				collabAgreeRequired_V: null,
				comprehensivePrescriptiveAuthority_V: null,
				comprehensiveSampleEligibility_V: null,
				ctrlSubstancePrescriptive_V: null,
				ctrlSubstanceSample_V: null,
				deaBusinessActivityCode_V: null,
				deaPaymentIndicator_V: null,
				drugSchedule_V: null,
				gracePeriod_V: null,
				licenseCertification_V: null,
				licenseDegree_V: null,
				licenseEligibility_V: null,
				licenseStatusCondition_V: null,
				licenseStatus_V: null,
				licenseSubtype_V: null,
				rxaEligible_V: null,
				sourceAddress_V: null,
				type_V: null,
				typeValue_V: null
			},
			{ shouldFocus: false }
		);
	};

	return (
		<FormSection title={t('Licenses')} id='licenses'>
			<Box sx={sx.root}>
				{fields.length > 0 && (
					<Box sx={sx.cardsContainer}>
						{fields.map((field, index) => {
							const originalData = initialSnapshots?.[field.vid_V ?? ''];
							const isNewCard = !originalData;

							return (
								<LicensesSectionCard
									key={field.key}
									index={index}
									onRemove={() => remove(index)}
									canBeRemoved={isNewCard}
									isExpanded={isNewCard}
									originalData={originalData}
									isNewCard={isNewCard}
									isLastItem={index === fields.length - 1}
								/>
							);
						})}
					</Box>
				)}

				<Box sx={sx.addNewCardButton}>
					<AddFormFieldButton onClick={handleAddLicense} />
				</Box>
			</Box>
		</FormSection>
	);
};
